import { Camelized, camelizeKeys } from "humps";
import orderBy from "lodash/orderBy";
import take from "lodash/take";
import { useCustomValuePreviewActions } from "PFApp/components/custom_value_preview/use_custom_value_preview";
import { useCustomValueTags } from "PFApp/hooks/use_custom_value_tags";
import { useExperienceConfig } from "PFApp/hooks/use_experience_config";
import { useExperienceLabelMap } from "PFApp/hooks/use_experience_label_map";
import { CustomValuePill } from "PFComponents/custom_value_pill";
import { isRankable } from "PFCore/helpers/custom_type";
import { useCustomValueToString } from "PFCore/helpers/custom_value";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { ActivityCustomValue, ActivityPureCustomValue, CustomValueScope, Id } from "PFTypes";

import { CustomValuesListCommonProps, DEFAULT_LIMIT } from "./custom_values_list.consts";
import css from "./custom_values_list.module.scss";
import { useCustomValuesVisibility } from "./hooks/use_custom_values_visibility";
import { CustomValuesDivider } from "./parts/custom_values_divider";
import { CustomValuesWrapper } from "./parts/custom_values_wrapper";

export type ActivityCustomValuesListProps = CustomValuesListCommonProps & {
  customValues: (ActivityCustomValue | ActivityPureCustomValue)[];
  activityId: Id;
};

export const ActivityCustomValuesList = ({
  customFieldName,
  customValues,
  activityId,
  canEdit = false,
  limit = DEFAULT_LIMIT,
  dark = false,
  vertical = false,
  maxNameLength,
  onMoreLessButtonClick
}: ActivityCustomValuesListProps) => {
  const { getCustomTypeByName } = useCustomTypes();
  const { showPreview } = useCustomValuePreviewActions();
  const { experienceLevels } = useExperienceConfig();
  const experienceLabelMap = useExperienceLabelMap();
  const { getCustomValueTags } = useCustomValueTags();
  const { getCustomValueString } = useCustomValueToString();

  const sortedCustomValues = orderBy(customValues, "value");

  const { visibleAvailableCustomValuesCount, showAll, displayShowMoreButton, toggleShowAll } =
    useCustomValuesVisibility({
      availableCustomValuesCount: sortedCustomValues.length,
      missingCustomValuesCount: 0,
      limit
    });

  const handleMoreLessButtonClick = () => {
    toggleShowAll();
    onMoreLessButtonClick?.();
  };

  const customType = getCustomTypeByName(customFieldName);
  const isCustomTypeRankable = Boolean(customType && isRankable(customType));

  return (
    <CustomValuesWrapper
      displayShowMoreButton={displayShowMoreButton}
      showAll={showAll}
      dark={dark}
      limit={limit}
      vertical={vertical}
      onMoreLessButtonClick={handleMoreLessButtonClick}
    >
      {take(sortedCustomValues, visibleAvailableCustomValuesCount).map((customValue, index) => {
        const isLastCustomValue = index === visibleAvailableCustomValuesCount - 1;
        // TODO: [PROF-5746] Remove "camelizeKeys" when all component usages get camelized customValues list
        const camelizedCustomValue = camelizeKeys(customValue) as Camelized<
          ActivityCustomValue | ActivityPureCustomValue
        >;

        return (
          <span className={css.customValueWithDivider} key={camelizedCustomValue.id}>
            <CustomValuePill
              isRequirement
              experience={isCustomTypeRankable ? camelizedCustomValue.requiredExperience : undefined}
              name={getCustomValueString(customValue, customType?.value_type)}
              experienceLevels={experienceLevels}
              experienceLabelMap={experienceLabelMap}
              theme={dark ? "dark" : "light"}
              maxNameLength={maxNameLength}
              tags={getCustomValueTags(camelizedCustomValue.tagIds)}
              onClick={() => {
                showPreview(
                  {
                    scope: CustomValueScope.Activity,
                    customTypeId: customType!.id,
                    customValueId: camelizedCustomValue.id,
                    activityId,
                    canEdit
                  },
                  true
                );
              }}
            />
            {!isLastCustomValue && !vertical && <CustomValuesDivider />}
          </span>
        );
      })}
    </CustomValuesWrapper>
  );
};
